import React from 'react'
import { Badge } from '../../../atoms'
import { IconButton } from '../../../atoms/IconButton'
import { Icon } from '../../../atoms/Icon'
import {
  Grid,
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
  ChartContainer,
} from '../GeoAnalysisSection.styles'
import { calculateTerrainMetrics } from '../utils'
import type { TopographyTabProps } from '../GeoAnalysisSection.types'

/**
 * Topography tab component showing 3D terrain visualization and flat area analysis
 */
export const TopographyTab: React.FC<TopographyTabProps> = ({
  altimetryData,
  terrainAltimetryData,
  altimetryLoading,
  topographyChartRef,
  translations,
  onDownloadTopography,
}) => {
  // LiDAR ground truth drives the slope/elevation NUMBERS when present; the 3D surface + Data Points
  // count below stay on `altimetryData` (the API altimetry grid).
  const terrainData = terrainAltimetryData ?? altimetryData
  const terrainMetrics = terrainData
    ? calculateTerrainMetrics(terrainData)
    : null

  // Altitudes arrive as raw floats — 482.73000011444094 is not a readable metre.
  const formatAltitude = (value: number) => `${Number(value).toFixed(1)} m`

  return (
    <Grid $columns={1}>
      <Card $span={1}>
        <CardHeader>
          <CardTitle>
            {translations?.sections?.topographicVisualization ||
              '3D Topographic Visualization'}
          </CardTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {altimetryData && (
              <Badge variant="success">
                {translations?.status?.realData || 'Real Elevation Data'}
              </Badge>
            )}
            {altimetryLoading && (
              <Badge variant="info">
                {translations?.status?.loading || 'Loading...'}
              </Badge>
            )}
            {!altimetryData && !altimetryLoading && (
              <Badge variant="warning">
                {translations?.status?.noData || 'No Data'}
              </Badge>
            )}
            {altimetryData && onDownloadTopography && (
              <IconButton
                size="small"
                variant="secondary"
                onClick={onDownloadTopography}
                aria-label="Download chart as PNG"
                title="Download PNG"
              >
                <Icon name="download" size="small" />
              </IconButton>
            )}
          </div>
        </CardHeader>

        {altimetryLoading ? (
          <div
            style={{
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
            }}
          >
            {translations?.loading || 'Loading elevation data...'}
          </div>
        ) : altimetryData ? (
          <ChartContainer ref={topographyChartRef} />
        ) : (
          <div
            style={{
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
            }}
          >
            {translations?.noElevationData ||
              'No elevation data available for this region'}
          </div>
        )}

        {altimetryData && (
          <MetricGrid style={{ marginTop: '20px' }}>
            <Grid $columns={4}>
              <MetricRow style={{ borderBottom: 'none' }}>
                <div>
                  <MetricLabel>
                    {translations?.metrics?.minAltitude || 'Min Altitude'}
                  </MetricLabel>
                  <MetricValue>
                    {formatAltitude(
                      (terrainData ?? altimetryData).altitude_min
                    )}
                  </MetricValue>
                </div>
              </MetricRow>
              <MetricRow style={{ borderBottom: 'none' }}>
                <div>
                  <MetricLabel>
                    {translations?.metrics?.maxAltitude || 'Max Altitude'}
                  </MetricLabel>
                  <MetricValue>
                    {formatAltitude(
                      (terrainData ?? altimetryData).altitude_max
                    )}
                  </MetricValue>
                </div>
              </MetricRow>
              <MetricRow style={{ borderBottom: 'none' }}>
                <div>
                  <MetricLabel>
                    {translations?.metrics?.averageSlope || 'Average Slope'}
                  </MetricLabel>
                  <MetricValue>
                    {terrainMetrics?.averageSlope.toFixed(1) ||
                      translations?.status?.na ||
                      'N/A'}
                    °
                  </MetricValue>
                </div>
              </MetricRow>
              <MetricRow style={{ borderBottom: 'none' }}>
                <div>
                  <MetricLabel>
                    {translations?.metrics?.dataPoints || 'Data Points'}
                  </MetricLabel>
                  <MetricValue>
                    {altimetryData.altimetry_grid.length}
                  </MetricValue>
                </div>
              </MetricRow>
            </Grid>
          </MetricGrid>
        )}
      </Card>
    </Grid>
  )
}
